import { Eye, EyeOff, RefreshCw } from 'lucide-react'

import { Button, SelectField, StatusMessage } from '@krp/ui-kit'

import { DATA_KIND_LABELS, IGNORE_LEVEL_LABELS, formatMoment } from '../model/labels'
import { DATA_KINDS, type DataKind, type Ignore, type ShownError } from '../model/types'
import { ErrorBlock } from './parts'
import './sync.css'

export interface IgnoresVm {
  status: 'loading' | 'failed' | 'ready'
  loadError: ShownError | null
  items: Ignore[]
  sources: { resourceId: string; name: string }[]
  nameOf: (resourceId: string) => string
  /** Форма «весь источник по виду данных». */
  draftSource: string
  draftKind: DataKind
  addBusy: boolean
  addError: ShownError | null
  busyIds: ReadonlySet<number>
  rowErrors: ReadonlyMap<number, ShownError>
}

export interface IgnoresActions {
  restore: (id: number) => void
  draftSourceChanged: (resourceId: string) => void
  draftKindChanged: (kind: DataKind) => void
  add: () => void
  reload: () => void
}

function RowError({ error }: { error: ShownError | undefined }) {
  return error === undefined ? null : <ErrorBlock error={error} />
}

/** Что именно не отслеживается: значение набора, запись источника или общий товар. */
function whatOf(item: Ignore): string | null {
  if (item.subject !== null) return item.subject
  if (item.remoteKey !== null) return `запись ${item.remoteKey}`
  if (item.productId !== null) return 'общий товар'
  return null
}

/**
 * Что не отслеживается (требования 9, 16): запись, товар по виду данных или весь источник по виду данных. Всё видно списком и
 * возвращается обратно кнопкой «Вернуть» — после неё строки снова появятся в расхождениях.
 */
export function IgnoresScreen({ vm, actions }: { vm: IgnoresVm; actions: IgnoresActions }) {
  const noSource = vm.draftSource === '' ? 'Выберите источник.' : null
  return (
    <section aria-labelledby="sync-ignores-title" className="sync">
      <header>
        <h2 id="sync-ignores-title">Не отслеживается</h2>
        <p className="sync__lead">По этим позициям и источникам система не показывает расхождений. «Вернуть» снова включает их в сравнение.</p>
      </header>

      <section className="sync__panel" aria-label="Не отслеживать вид данных у источника целиком">
        <h3>Весь источник по виду данных</h3>
        <p className="sync__note">Например, «для AQSI штрихкоды не синхронизируем».</p>
        <div className="sync__row">
          <SelectField
            label="Источник"
            value={vm.draftSource}
            options={[{ value: '', label: 'Не выбран' }, ...vm.sources.map((source) => ({ value: source.resourceId, label: source.name }))]}
            onChange={actions.draftSourceChanged}
            actionId="sync.ignores.source"
          />
          <SelectField
            label="Вид данных"
            value={vm.draftKind}
            options={DATA_KINDS.map((kind) => ({ value: kind, label: DATA_KIND_LABELS[kind] }))}
            onChange={(value) => actions.draftKindChanged(DATA_KINDS.find((kind) => kind === value) ?? 'item')}
            actionId="sync.ignores.kind"
          />
          <Button variant="secondary" icon={<EyeOff size={16} aria-hidden="true" />} busy={vm.addBusy} disabled={noSource !== null} disabledReason={noSource} onClick={actions.add} actionId="sync.ignores.add">
            Не отслеживать
          </Button>
        </div>
        {vm.addError !== null && <ErrorBlock error={vm.addError} />}
      </section>

      {vm.status === 'loading' && <StatusMessage kind="loading" title="Загружаем список…" />}
      {vm.status === 'failed' && vm.loadError !== null && (
        <ErrorBlock
          error={vm.loadError}
          action={
            <Button variant="secondary" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload} actionId="sync.ignores.reload">
              Повторить
            </Button>
          }
        />
      )}
      {vm.status === 'ready' &&
        (vm.items.length === 0 ? (
          <StatusMessage kind="empty" title="Всё отслеживается." />
        ) : (
          <ul className="sync-list" aria-label="Не отслеживается">
            {vm.items.map((item) => {
              const what = whatOf(item)
              return (
                <li key={item.id} className="sync__panel">
                  <div className="sync__row">
                    <span className="sync-badge">
                      <EyeOff size={14} aria-hidden="true" />
                      {IGNORE_LEVEL_LABELS[item.level]}
                    </span>
                    <strong>{vm.nameOf(item.resourceId)}</strong>
                    <span>{DATA_KIND_LABELS[item.dataKind]}</span>
                    {what !== null && <span className="sync__note">{what}</span>}
                  </div>
                  <div className="sync__row">
                    <span className="sync__note">
                      {item.createdBy}, {formatMoment(item.createdAt)}
                    </span>
                    <Button variant="ghost" icon={<Eye size={16} aria-hidden="true" />} busy={vm.busyIds.has(item.id)} onClick={() => actions.restore(item.id)} actionId="sync.ignores.restore">
                      Вернуть
                    </Button>
                  </div>
                  <RowError error={vm.rowErrors.get(item.id)} />
                </li>
              )
            })}
          </ul>
        ))}
    </section>
  )
}
